import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  DeadlineKind,
  ReturnPeriodRule,
  deadlineValid
} from '@/domain/return-conditions'
import {
  OpenKey,
  SetOpenKey,
  ValuePopover,
  bindOpen
} from '@/ui/components/rule-list'
import { NS, formatDay, toDate, toIso } from './utils'

const {
  Calendar,
  FieldRoot,
  FieldLabel,
  FieldDescription,
  FieldError,
  Input,
  ToggleGroup,
  ToggleGroupItem
} = Base

const KINDS: DeadlineKind[] = ['until', 'days']

export const DeadlineCell: React.FC<{
  id: string
  rule: ReturnPeriodRule
  n: number
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (rule: ReturnPeriodRule) => void
}> = ({ id, rule, n, editable, openKey, setOpenKey, onChange }) => {
  const { t, i18n } = useTranslation()
  const { deadline } = rule
  const valid = deadlineValid(rule)
  const setDeadline = (next: Partial<ReturnPeriodRule['deadline']>) =>
    onChange({ ...rule, deadline: { ...deadline, ...next } })

  const triggerClassName =
    'inline-flex w-44 shrink-0 items-center justify-end rounded-md px-2 py-1 text-sm font-medium tabular-nums outline-none ' +
    (editable ? 'hover:bg-neutral-100 focus-visible:bg-neutral-100 ' : '') +
    (valid ? 'text-neutral-900' : 'text-red-500')
  const triggerContent = !valid
    ? t(`${NS}.set_deadline`)
    : deadline.kind === 'until'
      ? t(`${NS}.until`, { date: formatDay(deadline.date, i18n.language) })
      : t(`${NS}.days`, { count: deadline.days ?? 0 })

  if (!editable)
    return <span className={triggerClassName}>{triggerContent}</span>
  return (
    <ValuePopover
      {...bindOpen(`${id}:deadline`, openKey, setOpenKey)}
      triggerLabel={t(`${NS}.deadline_label`, { n })}
      triggerClassName={triggerClassName}
      triggerContent={triggerContent}
      className="w-auto"
    >
      <div className="flex flex-col gap-3 p-3">
        <ToggleGroup
          variant="button"
          aria-label={t(`${NS}.column_deadline`)}
          value={[deadline.kind]}
          // Single choice: pressing the selected option again keeps it selected.
          onValueChange={(next) =>
            next.length > 0 && setDeadline({ kind: next[0] as DeadlineKind })
          }
        >
          {KINDS.map((kind) => (
            <ToggleGroupItem key={kind} value={kind}>
              {t(`${NS}.kind_${kind}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {deadline.kind === 'until' ? (
          <FieldRoot invalid={!valid}>
            <FieldLabel>{t(`${NS}.last_day`)}</FieldLabel>
            <Calendar
              mode="single"
              defaultMonth={toDate(deadline.date) ?? toDate(rule.end)}
              selected={toDate(deadline.date)}
              onSelect={(date) => setDeadline({ date: toIso(date) })}
            />
            {valid ? null : (
              <FieldError match>{t(`${NS}.last_day_error`)}</FieldError>
            )}
          </FieldRoot>
        ) : (
          <FieldRoot invalid={!valid} className="w-64">
            <FieldLabel>{t(`${NS}.days_label`)}</FieldLabel>
            <FieldDescription>{t(`${NS}.days_hint`)}</FieldDescription>
            <Input
              autoFocus
              inputMode="numeric"
              value={deadline.days ?? ''}
              onChange={(event) => {
                const value = event.target.value.trim()
                const days = Number(value)
                setDeadline({ days: value === '' || isNaN(days) ? null : days })
              }}
              onKeyDown={(event) => event.key === 'Enter' && setOpenKey(null)}
            />
          </FieldRoot>
        )}
      </div>
    </ValuePopover>
  )
}
